/**
 * Utilities for masking and formatting credit card
 *
 * Usage:
 * <Field name="creditcard" type="text" placeholder="Credit Card"  v-model="creditcardnumber" v-on:input="formatCC" v-on:blur="maskCC"/>
 *  methods: {
 *      maskCC: function () {
 *          this.creditcardnumber = cardNumberMask(this.creditcardnumber, 4);
 *      }
 *      formatCC: function () {
 *          this.creditcardnumber = cardNumberFormat(this.creditcardnumber, 'A');
 *      }
 * }
 */

/**
 * @feature/add-card-wallet-overlay AddCardView.js
 *
 */
const getCardMask = function (cardType) {
  if (cardType === 'A' || cardType === 'B') {
    return '9999 999999 99999';
  } if (cardType === 'Y' || cardType === 'V' || cardType === 'M' || cardType === 'O' || cardType === 'F') {
    return '9999 9999 9999 9999';
  }
  return '9999999999999999';
};

/**
 * https://github.com/vanilla-masker/vanilla-masker/blob/master/lib/vanilla-masker.js
 */
const DIGIT = '9';
const ALPHA = 'A';
const ALPHANUM = 'S';
const addPlaceholdersToOutput = function (output, indexInput, placeholder) {
  let index = indexInput;
  for (; index < output.length; index += 1) {
    if (output[index] === DIGIT || output[index] === ALPHA || output[index] === ALPHANUM) {
      output[index] = placeholder;
    }
  }
  return output;
};

const toPattern = function (value, opts) {
  const pattern = (typeof opts === 'object' ? opts.pattern : opts);
  const patternChars = pattern.replace(/\W/g, '');
  const output = pattern.split('');
  const values = value.toString().replace(/\W/g, '');
  const charsValues = values.replace(/\W/g, '');
  let index = 0;
  let i;
  const outputLength = output.length;
  const placeholder = (typeof opts === 'object' ? opts.placeholder : undefined);
  for (i = 0; i < outputLength; i += 1) {
    // Reached the end of input
    if (index >= values.length) {
      if (patternChars.length === charsValues.length) {
        return output.join('');
      } if ((placeholder !== undefined) && (patternChars.length > charsValues.length)) {
        return addPlaceholdersToOutput(output, i, placeholder).join('');
      }

      break;
    } else if ((output[i] === DIGIT && values[index].match(/[0-9]/))
            || (output[i] === ALPHA && values[index].match(/[a-zA-Z]/))
            || (output[i] === ALPHANUM && values[index].match(/[0-9a-zA-Z]/))) {
      output[i] = values[index];
      index += 1;
    } else if (output[i] === DIGIT || output[i] === ALPHA || output[i] === ALPHANUM) {
      if (placeholder !== undefined) {
        return addPlaceholdersToOutput(output, i, placeholder).join('');
      }

      return output.slice(0, i).join('');

      // exact match for a non-magic character
    } else if (output[i] === values[index]) {
      index += 1;
    }
  }
  return output.join('').substr(0, i);
};

/**
 * Format credit card number, based on cardType
 */
const cardNumberFormat = function (cardNumber, cardType) {
  const cardInputMask = getCardMask(cardType);

  return toPattern(cardNumber, cardInputMask);
};

/*
 * Format the phone number
 * Example phoneInputMask '(999) 999-9999'
 */
const phoneFormat = function (phoneNumber, phoneInputMask = '999-999-9999') {
  return toPattern(phoneNumber, phoneInputMask);
};

/*
 Format of currency patterns 99,999,9999,99.99,999.99
*/
function generateCurrencyPattern(value) {
  let pattern = '';
  const indexOfDot = value.indexOf('.');
  const beforeDotValue = indexOfDot > 0 ? value.substring(0, indexOfDot) : value;
  const patternChars = beforeDotValue.replace(/\W/g, '');
  for (let i = 0; i < patternChars.length; i++) {
    pattern += '9';
  }
  pattern = (patternChars.length > 0 ? '$' : '') + pattern + (indexOfDot > 0 ? '.99' : '');
  return pattern;
}

const currencyFormat = function (inputValue, currencyInputMask = generateCurrencyPattern(inputValue)) {
  return toPattern(inputValue, currencyInputMask);
};

/**
 * Replace all characters except the last few with *
 */
const NUMBER_OF_CHARACTERS_TO_SHOW = 4;
const PATTERN = `.(?=.{${NUMBER_OF_CHARACTERS_TO_SHOW},}$)`;
const EXPRESSION = new RegExp(PATTERN, 'g');
const cardNumberMask = function (cardNumber, showNoOfChars) {
  if (!Number.isNaN(showNoOfChars)) {
    const numberOfCharactersToShow = parseInt(showNoOfChars, 10);
    const pattern = `.(?=.{${numberOfCharactersToShow},}$)`;
    const expression = new RegExp(pattern, 'g');
    return cardNumber.replace(expression, '*');
  }

  return cardNumber.replace(EXPRESSION, '*');
};

module.exports = {
  cardNumberMask, cardNumberFormat, phoneFormat, currencyFormat,
};
